/**
 * HowItWorks — 3-step visual journey for absolute newcomers, placed
 * after the cinematic hero. Answers the 3-second question "what even
 * is this site?" before they dive into the data pages.
 *
 * Pattern: big icon → punchy label → 1-sentence what-it-does → expanded
 * paragraph that fades in on hover/tap. Animated connection lines
 * between steps create flow.
 */
import React from "react";
import { motion } from "framer-motion";
import {
  GlobeHemisphereEast, Atom, Sun, ArrowRight, Sparkle,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const STEPS = [
  {
    n: "01",
    Icon: GlobeHemisphereEast,
    color: "#3D7A8C",
    bg: "linear-gradient(135deg, rgba(77,163,255,0.10) 0%, #FFFFFF 70%)",
    label: "Satelliten fotografieren",
    headline: "Alle 3-5 Tage. Jedes Feld.",
    body: "Die EU-Satelliten Sentinel-2 fliegen seit 2015 in 786 km Höhe und nehmen kostenlos hochaufgelöste Bilder von ganz Europa auf. Wir holen sie uns aus dem Copernicus-Archiv — die größte öffentliche Erdbeobachtungs-Datenbank der Welt.",
    fact: "10 m Auflösung · 13 Farbkanäle · seit 2015 frei verfügbar",
  },
  {
    n: "02",
    Icon: Atom,
    color: "#D9A87C",
    bg: "linear-gradient(135deg, #16130D 0%, #FFFFFF 70%)",
    label: "Wir rechnen alles um",
    headline: "Aus 13 Wellenlängen werden 4 Zahlen.",
    body: "NDVI (Grün-Index), Bodenfeuchte, ET₀ (Wasserverlust) und Temperatur — die 4 Kennzahlen, die wirklich zählen. Open-Meteo gibt uns Wetter aus 13 Modellen, NASA FIRMS Waldbrände, ERA5 das Klima-Archiv seit 1940.",
    fact: "Updates alle 6 Stunden · Cache im Hintergrund · keine Wartezeit",
  },
  {
    n: "03",
    Icon: Sun,
    color: "#FFC24B",
    bg: "linear-gradient(135deg, #241C06 0%, #FFFFFF 70%)",
    label: "Du liest in Klartext",
    headline: `„Solides Grün. Boden hat Wasser."`,
    body: `Statt 0.62 m³/m³ schreiben wir: „Im grünen Bereich — Pflanzen haben genug Wasser." Jede Zahl bekommt eine Skala, einen Pointer und einen Satz auf B1-Niveau. Damit jede:r mitlesen kann — egal ob Profi oder neugierig.`,
    fact: "Klartext pro Wert · Tooltips für Fachbegriffe · 0 € · 0 Werbung",
  },
];

function StepCard({ step, idx }) {
  const Icon = step.Icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="relative border border-white/15 p-6 sm:p-7 group hover:shadow-[0_8px_30px_rgba(0,0,0,0.5)] transition-shadow"
      style={{ background: step.bg }}
      data-testid={`how-step-${idx + 1}`}
    >
      {/* Step number ribbon */}
      <div
        className="absolute top-0 right-0 px-3 py-1 text-[10px] font-mono font-bold tracking-widest text-white"
        style={{ background: step.color }}
      >
        {step.n}
      </div>
      <div
        className="w-14 h-14 rounded-sm flex items-center justify-center mb-4"
        style={{ background: step.color, color: "#FFFFFF" }}
        aria-hidden="true"
      >
        <Icon size={28} weight="fill" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold mb-1.5" style={{ color: step.color }}>
        {step.label}
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight leading-tight">
        {step.headline}
      </h3>
      <p className="mt-3 text-sm text-white/80 leading-relaxed">
        {step.body}
      </p>
      <div
        className="mt-4 pt-3 border-t text-[11px] font-mono uppercase tracking-widest"
        style={{ borderColor: `${step.color}30`, color: step.color }}
      >
        {step.fact}
      </div>
    </motion.div>
  );
}

export default function HowItWorks() {
  return (
    <section data-testid="how-it-works" className="space-y-6">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="eid-label inline-flex items-center gap-1.5 text-[#00FA9A]">
            <Sparkle size={11} weight="fill" /> Noch nie hier gewesen?
          </div>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-1.5 leading-[0.9] max-w-3xl">
            So funktioniert Bodenblick — in 60 Sekunden.
          </h2>
          <p className="text-sm sm:text-base text-white/70 mt-3 max-w-2xl leading-relaxed">
            Wir nehmen freie Daten der EU-Satelliten, des Deutschen Wetterdiensts und
            des Open-Meteo-Modells, rechnen sie zu vier verständlichen Werten zusammen
            und übersetzen jeden in einen Satz, den jede:r versteht. Das war's. Kein
            Login. Keine Werbung. Kein Premium.
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Connector arrows on desktop only */}
        <div className="hidden md:block absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="grid grid-cols-3 h-full">
            <div className="relative">
              <ArrowRight
                size={28}
                weight="bold"
                className="absolute top-12 -right-3 text-white/25"
              />
            </div>
            <div className="relative">
              <ArrowRight
                size={28}
                weight="bold"
                className="absolute top-12 -right-3 text-white/25"
              />
            </div>
            <div />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative">
          {STEPS.map((s, i) => <StepCard step={s} idx={i} key={s.n} />)}
        </div>
      </div>

      <Link
        to="/dashboard"
        data-testid="how-cta"
        className="inline-flex items-center gap-2 bg-[#F5F5F5] text-[#0A0A0A] px-5 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#00FA9A]/20 transition-colors rounded-sm"
      >
        Live-Daten im Daten-Cockpit ansehen <ArrowRight size={14} weight="bold" />
      </Link>
    </section>
  );
}
